import React, { useMemo, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useFormikContext } from "formik";

import sortBy from "lodash/sortBy";
import snakeCase from "lodash/snakeCase";

import Autocomplete from "src/components/form/Autocomplete";
import Input from "src/components/form/Input";
import useAuth from "src/hooks/useAuth";
import { ProjectContext } from "src/contexts/project";
import {
  basicAdvancedSearch,
  medicalAdvancedSearch,
} from "src/constants/advancedSearchFieldOptions";
import { EMPTY_STRING_SEARCH_TOKEN } from "src/constants/search";

export const OSD_SEARCH_FIELD = "has_osd";
const OSD_SEARCH_OPTION = { label: "OS&D", value: OSD_SEARCH_FIELD };
const OSD_SEARCH_VALUES = [
  { label: "Yes", value: "true" },
  { label: "Damage", value: "Damage" },
  { label: "Shortage", value: "Shortage" },
  { label: "Overage", value: "Overage" },
];

// Mirrors the BE normalize_label method in assets_query.rb
export const normalizeLabel = label =>
  String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

const toSearchValue = field => {
  if (field.id.startsWith("customField")) {
    return normalizeLabel(field.label || field.id);
  }
  return snakeCase(field.id);
};

// Convert mm/dd/yyyy or mm/yyyy to yyyy-mm-dd or yyyy-mm for backend
export const convertDateFormat = (dateStr) => {
  if (!dateStr) return dateStr;
  
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    // mm/dd/yyyy -> yyyy-mm-dd
    const [month, day, year] = parts;
    if (month && day && year && year.length === 4) {
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  } else if (parts.length === 2) {
    // mm/yyyy -> yyyy-mm
    const [month, year] = parts;
    if (month && year && year.length === 4) {
      return `${year}-${month.padStart(2, '0')}`;
    }
  }
  return dateStr; // Return as-is if format doesn't match
};

const styles = {
  container: {
    marginBottom: 1.5,
    padding: 1.5,
    borderRadius: 1,
    border: "1px solid",
    borderColor: "divider",
  },
  field: {
    marginBottom: 1.5,
  },
  toggleLabel: {
    marginBottom: 1,
  },
  toggleGroup: {
    display: "flex",
    justifyContent: "space-between",
  },
  toggleButton: {
    textTransform: "none",
    px: 1.5,
  },
};

const AdvancedSearchCriteria = ({ index, removeCriterion, fields }) => {
  const { setFieldValue, values } = useFormikContext();
  const { user } = useAuth();
  const { settings } = user.company;
  // Use useContext directly to gracefully handle cases where project context isn't available
  const project = useContext(ProjectContext);

  const handleToggleChange = (event, newMatchType) => {
    if (newMatchType !== null) {
      setFieldValue(`criteria[${index}].matchType`, newMatchType);
    }
  };

  const currentCriterion = values.criteria[index] || {};
  const isEmptySearchSelected = currentCriterion.text === EMPTY_STRING_SEARCH_TOKEN;
  const isSearchFieldSelected = Boolean(currentCriterion.field);
  const isOsdSearchField = currentCriterion.field === OSD_SEARCH_FIELD;

  useEffect(() => {
    if (isOsdSearchField && currentCriterion.matchType !== "=") {
      setFieldValue(`criteria[${index}].matchType`, "=");
    }
  }, [currentCriterion.field, currentCriterion.matchType, index, isOsdSearchField, setFieldValue]);

  const handleEmptySearchClick = () => {
    setFieldValue(
      `criteria[${index}].text`,
      isEmptySearchSelected ? "" : EMPTY_STRING_SEARCH_TOKEN
    );
  };

  const emptyChip = isSearchFieldSelected && (!currentCriterion.text || isEmptySearchSelected) ? (
    <InputAdornment position="end">
      <Chip
        label="Empty"
        size="small"
        clickable
        color={isEmptySearchSelected ? "primary" : "default"}
        variant={isEmptySearchSelected ? "filled" : "outlined"}
        onMouseDown={handleEmptySearchClick}
        sx={{ cursor: "pointer" }}
      />
    </InputAdornment>
  ) : null;

  const renderEmptySelectedInput = () => (
    <TextField
      fullWidth
      label="Search"
      value=""
      placeholder="Empty"
      size="small"
      InputProps={{
        endAdornment: emptyChip,
        readOnly: true,
      }}
    />
  );

  const getOptions = () => {
    if (fields) {
      const mapped = fields
        .filter(
          f =>
            f.visible &&
            !f.inactive &&
            f.type !== "checkbox" &&
            f.type !== "photo" &&
            !f.disableFillDown
        )
        .filter(f => ![
          "itemId",
          "photoCarousel",
          "rackLocations",
          "BULK_CHECKBOX",
          "photoUpload",
          "warehousesFromRackLocations",
          // Calculated/cached fields without DB columns
          "warehouseQuantity",
          "allocatedQuantity",
          "reservedQuantity",
          "inCartQuantity",
          "daysLeftToExpire",
        ].includes(f.id))
        .map(f => ({
          label: f.label || f.id,
          value: toSearchValue(f),
        }));
      if (!mapped.some(option => option.value === OSD_SEARCH_FIELD)) {
        mapped.push(OSD_SEARCH_OPTION);
      }
      return sortBy(mapped, [o => (o?.label || "").toLowerCase()]);
    }

    return settings.medical_fields
      ? sortBy([...basicAdvancedSearch, ...medicalAdvancedSearch], [o => (o?.label || "").toLowerCase()])
      : basicAdvancedSearch;
  };

  // Determine field type for special input handling
  const fieldType = useMemo(() => {
    const selectedFieldValue = values.criteria[index]?.field;
    
    // Timestamp fields that need conversion to yyyy-mm-dd
    const timestampFields = ['created_at', 'updated_at', 'last_in', 'last_out'];
    if (timestampFields.includes(selectedFieldValue)) {
      return { type: 'timestamp', format: 'MM/DD/YYYY or MM/YYYY' };
    }
    
    // Expiration and manufacture date fields (strings, no conversion needed)
    const dateFormatMap = {
      'expiration_month_day_year': { type: 'date', format: 'MM/DD/YYYY', maxLength: 10 },
      'manufacture_date': { type: 'date', format: 'MM/DD/YYYY', maxLength: 10 },
      'expiration_date': { type: 'date', format: 'MM/YYYY', maxLength: 7 },
      'expiration_month': { type: 'month', format: 'MM', maxLength: 2 },
      'expiration_year': { type: 'year', format: 'YYYY', maxLength: 4 },
    };
    
    return dateFormatMap[selectedFieldValue] || null;
  }, [values.criteria[index]?.field]);

  // Get predefined options for fields with restricted values
  const getPredefinedOptions = useMemo(() => {
    const selectedFieldValue = values.criteria[index]?.field;
    if (selectedFieldValue === OSD_SEARCH_FIELD) {
      return OSD_SEARCH_VALUES;
    }
    if (!selectedFieldValue || !project) return null;

    // Map field values to their corresponding project data
    const fieldOptionsMap = {
      id_cost_center: project.costCenters?.filter(cc => cc.costCenterName).map(cc => ({
        label: cc.costCenterName,
        value: cc.costCenterName,
      })),
      pallet_type: project.palletTypes?.filter(pt => pt.isActive && pt.id).map(pt => ({
        label: pt.name,
        value: pt.id,
      })),
    };

    return fieldOptionsMap[selectedFieldValue] || null;
  }, [values.criteria[index]?.field, project]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.field}>
        <Autocomplete
          name={`criteria[${index}].field`}
          options={getOptions()}
          label="Search Field"
          placeholder="Select a Field"
          showNAButton={false}
        />
      </Box>
      <Box sx={styles.field}>
        {isEmptySearchSelected ? (
          renderEmptySelectedInput()
        ) : getPredefinedOptions ? (
          <Autocomplete
            name={`criteria[${index}].text`}
            options={getPredefinedOptions}
            label="Search"
            placeholder="Select a value"
            showNAButton={false}
            endAdornment={emptyChip}
          />
        ) : fieldType ? (
          <Input
            name={`criteria[${index}].text`}
            label="Search"
            placeholder={fieldType.format}
            disabled={!currentCriterion.field}
            showNAButton={false}
            onChange={(name, value) => {
              let formattedValue = value.replace(/[^\d/]/g, '');
              
              // Handle different date formats
              if (fieldType.type === 'month') {
                // MM only (2 digits)
                formattedValue = formattedValue.slice(0, 2);
              } else if (fieldType.type === 'year') {
                // YYYY only (4 digits)
                formattedValue = formattedValue.slice(0, 4);
              } else if (fieldType.format === 'MM/YYYY') {
                // MM/YYYY format
                if (formattedValue.length >= 2 && formattedValue.charAt(2) !== '/') {
                  formattedValue = formattedValue.slice(0, 2) + '/' + formattedValue.slice(2);
                }
                if (formattedValue.length > 7) {
                  formattedValue = formattedValue.slice(0, 7);
                }
              } else if (fieldType.format === 'MM/DD/YYYY' || fieldType.format === 'MM/DD/YYYY or MM/YYYY') {
                // MM/DD/YYYY format
                if (formattedValue.length >= 2 && formattedValue.charAt(2) !== '/') {
                  formattedValue = formattedValue.slice(0, 2) + '/' + formattedValue.slice(2);
                }
                if (formattedValue.length >= 5 && formattedValue.charAt(5) !== '/') {
                  formattedValue = formattedValue.slice(0, 5) + '/' + formattedValue.slice(5);
                }
                if (formattedValue.length > 10) {
                  formattedValue = formattedValue.slice(0, 10);
                }
              }
              
              setFieldValue(name, formattedValue);
            }}
            inputProps={{
              maxLength: fieldType.maxLength,
            }}
            InputProps={{
              endAdornment: emptyChip,
            }}
          />
        ) : (
          <Input
            name={`criteria[${index}].text`}
            label="Search"
            disabled={!currentCriterion.field}
            showNAButton={false}
            InputProps={{
              endAdornment: emptyChip,
            }}
          />
        )}
      </Box>
      <Typography sx={styles.toggleLabel} fontWeight={500} color="textSecondary" variant="body2">
        Match Type
      </Typography>
      <Box sx={styles.toggleGroup}>
        <ToggleButtonGroup
          size="small"
          value={values.criteria[index].matchType}
          exclusive
          onChange={handleToggleChange}
          disabled={isOsdSearchField}
        >
          <ToggleButton value="~" sx={styles.toggleButton}>
            Contains
          </ToggleButton>
          <ToggleButton value=":" sx={styles.toggleButton}>
            Word Match
          </ToggleButton>
          <ToggleButton value="=" sx={styles.toggleButton}>
            Exact
          </ToggleButton>
        </ToggleButtonGroup>
        {values.criteria.length > 1 && (
          <IconButton size="small" onClick={() => removeCriterion(index)}>
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

AdvancedSearchCriteria.propTypes = {
  index: PropTypes.number.isRequired,
  removeCriterion: PropTypes.func.isRequired,
  fields: PropTypes.array,
};

export default AdvancedSearchCriteria;
